import React, { useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Send, Book, Square } from 'lucide-react';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { useIsMobile } from '@/hooks/use-mobile';
import { KnowledgeBase as SupabaseKnowledgeBase } from '@/types/supabase';

interface ChatInputFieldProps {
  message: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  hasAttachments: boolean;
  disableInput: boolean;
  disableSubmit: boolean;
  isGenerating?: boolean;
  onStopGeneration?: () => void;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSelectKnowledgeBase?: (kbId: string, kbName: string) => void;
}

const ChatInputField: React.FC<ChatInputFieldProps> = ({
  message,
  textareaRef,
  hasAttachments,
  disableInput,
  disableSubmit,
  isGenerating = false,
  onStopGeneration,
  onTextChange,
  onKeyDown,
  onSubmit,
  onSelectKnowledgeBase,
}) => {
  const { knowledgeBases, fetchKnowledgeBases } = useKnowledgeBases();
  const [showKbSuggestions, setShowKbSuggestions] = useState(false);
  const [filteredKnowledgeBases, setFilteredKnowledgeBases] = useState<SupabaseKnowledgeBase[]>([]);
  const [selectedKbIndex, setSelectedKbIndex] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  useEffect(() => {
    const kbCommandRegex = /^\/kb(?:\s+)?/i;
    if (kbCommandRegex.test(message)) {
      const searchTerm = message.replace(/^\/kb\s*/i, '').toLowerCase();
      const filtered = searchTerm
        ? knowledgeBases.filter((kb) => kb.name.toLowerCase().includes(searchTerm))
        : knowledgeBases;

      setFilteredKnowledgeBases(filtered);
      setShowKbSuggestions(true);
      setSelectedKbIndex(0);
      return;
    }

    setShowKbSuggestions(false);
  }, [knowledgeBases, message]);

  const selectKnowledgeBase = (kb: SupabaseKnowledgeBase) => {
    if (!onSelectKnowledgeBase) {
      return;
    }

    setShowKbSuggestions(false);
    if (textareaRef.current) {
      textareaRef.current.value = '';
      textareaRef.current.focus();
    }

    onSelectKnowledgeBase(kb.id, kb.name);
  };

  const handleKeyboardNavigation = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showKbSuggestions || filteredKnowledgeBases.length === 0) {
      onKeyDown(e);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedKbIndex((previous) => (previous + 1) % filteredKnowledgeBases.length);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedKbIndex((previous) => previous > 0 ? previous - 1 : filteredKnowledgeBases.length - 1);
      return;
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      selectKnowledgeBase(filteredKnowledgeBases[selectedKbIndex]);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setShowKbSuggestions(false);
      return;
    }

    onKeyDown(e);
  };

  return (
    <div className="relative w-full">
      <Textarea
        ref={textareaRef}
        placeholder={isMobile ? 'Type a message...' : 'Type a message or /kb to use a knowledge base...'}
        value={message}
        onChange={onTextChange}
        onKeyDown={handleKeyboardNavigation}
        disabled={disableInput}
        className={`min-h-[50px] resize-none py-3 ${isGenerating ? 'pr-24' : 'pr-16'}`}
        rows={1}
      />

      <div className="absolute bottom-2 right-2">
        {isGenerating && onStopGeneration ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-8 gap-1.5 rounded-full border border-amber-400/50 bg-amber-100 px-3 text-amber-950 hover:bg-amber-200 dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
            onClick={onStopGeneration}
          >
            <Square className="h-3 w-3 fill-current" />
            Stop
          </Button>
        ) : (
          <Button
            type="submit"
            size="sm"
            className="h-8 w-8 rounded-full p-0"
            disabled={disableSubmit || (!message.trim() && !hasAttachments)}
            onClick={onSubmit}
          >
            <Send size={16} className="text-white" />
          </Button>
        )}
      </div>

      {showKbSuggestions ? (
        <div className="absolute bottom-full z-10 mb-2 w-full max-w-md rounded-md border border-border bg-card shadow-md">
          <div className="border-b p-2 text-sm font-medium">Knowledge Bases</div>

          {filteredKnowledgeBases.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No knowledge bases found</div>
          ) : (
            <div className="max-h-60 overflow-y-auto">
              {filteredKnowledgeBases.map((kb, index) => (
                <div
                  key={kb.id}
                  className={`flex cursor-pointer items-center gap-2 p-2 text-sm ${
                    index === selectedKbIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                  onClick={() => selectKnowledgeBase(kb)}
                >
                  <Book size={16} />
                  <div>
                    <div className="font-medium">{kb.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {kb.documents_count} document{kb.documents_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ChatInputField;
